import { StoreCart, StorePromotion, PromotionType } from "@/types/store"
import { StoreCartItem } from "@/types/store"

const getItemProduct = (item: StoreCartItem) => {
  return item.product ?? item.variant?.product ?? null
}

const toNumber = (value: unknown) => {
  return typeof value === "number" ? value : undefined
}

const getItemPrice = (item: StoreCartItem) => {
  const variant = item.variant

  return (
    toNumber(variant?.sales_price) ??
    toNumber(variant?.unitPrice) ??
    toNumber(variant?.price) ??
    0
  )
}

const getItemQuantity = (item: StoreCartItem) => {
  return item.variant?.quantity ?? 0
}

const getItemDiscount = (
  item: StoreCartItem,
  promotion: StorePromotion | null,
  cart?: StoreCart | null
) => {
  if (!promotion) return 0

  const price = getItemPrice(item)
  const quantity = getItemQuantity(item)
  const product = getItemProduct(item)

  if (
    promotion.type === PromotionType.AmountOffProduct ||
    promotion.type === PromotionType.PercentageOffProduct
  ) {
    const productId = product?.id?.toString()
    if (!productId) return 0
    if (!promotion.valid_product_ids?.includes(productId)) return 0

    if (promotion.type === PromotionType.AmountOffProduct) {
      return Math.min(price, promotion.promotion_value)
    }
    return (price * promotion.promotion_value) / 100
  }

  if (
    promotion.type === PromotionType.AmountOffOrder ||
    promotion.type === PromotionType.PercentageOffOrder
  ) {
    if (!cart) return 0
    if (product && Number((product as any).discountable) === 0) return 0
    if (quantity === 0) return 0

    const cartItemSubtotal = cart.itemSubtotal || 0
    if (cartItemSubtotal === 0) return 0

    if (promotion.type === PromotionType.PercentageOffOrder) {
      return (price * promotion.promotion_value) / 100
    }

    const itemTotal = price * quantity
    const share = itemTotal / cartItemSubtotal
    return (promotion.promotion_value * share) / quantity
  }

  return 0
}

const calculateSubtotal = (cart: StoreCart | null) => {
  return cart?.itemSubtotal ?? 0
}

const calculateDiscountableSubtotal = (cart: StoreCart, promotion: StorePromotion) => {
  const restrictToIds = Boolean(promotion.valid_product_ids?.length)
  return cart.items
    .filter((item) => {
      const product = getItemProduct(item)
      if (product && Number((product as any).discountable) === 0) return false
      if (!restrictToIds) return true
      const productId = product?.id?.toString()
      return productId ? promotion.valid_product_ids!.includes(productId) : false
    })
    .reduce((sum, item) => {
      const quantity = getItemQuantity(item)
      if (quantity === 0) return sum
      return sum + getItemPrice(item) * quantity
    }, 0)
}

const calculateDiscountTotal = (
  cart: StoreCart | null,
  promotion: StorePromotion | null
) => {
  if (!promotion || !cart) return 0

  if (
    promotion.type === PromotionType.AmountOffOrder ||
    promotion.type === PromotionType.PercentageOffOrder
  ) {
    const discountableSubtotal = calculateDiscountableSubtotal(cart, promotion)
    if (promotion.type === PromotionType.AmountOffOrder) {
      return Math.min(discountableSubtotal, promotion.promotion_value)
    }
    return (discountableSubtotal * promotion.promotion_value) / 100
  }

  return cart.items.reduce((total, item) => total + getItemDiscount(item, promotion, cart), 0)
}

const calculateTotal = (subtotal: number, discountTotal: number) => {
  return subtotal - discountTotal
}

export {
  getItemDiscount,
  calculateSubtotal,
  calculateDiscountTotal,
  calculateTotal,
}
